// src/components/PlatformAdminView.jsx
// Read-only oversight dashboard across every org: org list, cohorts,
// student rosters, and counts. Deliberately never shows submissions,
// marking, or comment bank content - see firestore.rules' isPlatformAdmin()
// for the access boundary this relies on.
//
// Also includes the "Create New Tutor" form - an in-app equivalent of
// scripts/onboardNewOrg.js, calling the onboardNewOrg Cloud Function. The
// account is only ever created when this dashboard's operator clicks
// Create; nothing here runs unattended.

import React, { useEffect, useState, useCallback } from 'react';
import { ArrowLeft, LogOut, Users, GraduationCap, ChevronDown, ChevronRight, UserPlus, Copy, Check, LifeBuoy, Sparkles, CreditCard, Receipt, Trash2, RefreshCw } from 'lucide-react';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { getApp } from 'firebase/app';
import { signInWithCustomToken, setPersistence, browserSessionPersistence } from 'firebase/auth';
import { auth } from '../firebase/config';
import { getAllOrgs, getAllUserProfiles, getSupportAccessLog, getAllAiSummaryUsage, getTestAccountIds, setTestAccount, getAllRecentlyDeletedCohorts, adminCohortAction } from '../firebase/platformAdmin';
import { getAllCohorts, COHORT_DELETION_RETENTION_DAYS } from '../firebase/cohorts';
import BillingReport from './BillingReport';
import InvoicingPanel from './InvoicingPanel';
import { DEFAULT_ORG_ID } from '../firebase/orgs';

const SUPPORT_SESSION_KEY = 'homeworkSolutions.supportSession';

const emptyForm = { orgId: '', orgName: '', headMarkerName: '', headMarkerEmail: '', cohortName: '', cohortCode: '' };

function PlatformAdminView({ user, onExit, onLogout }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [orgs, setOrgs] = useState([]);
  const [expandedCohorts, setExpandedCohorts] = useState({});

  const [showCreateForm, setShowCreateForm] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState('');
  const [createResult, setCreateResult] = useState(null);
  const [copied, setCopied] = useState(false);

  const [supportLog, setSupportLog] = useState([]);
  const [enteringOrgId, setEnteringOrgId] = useState(null);
  const [enterError, setEnterError] = useState({});

  const [regeneratingEmail, setRegeneratingEmail] = useState(null);
  const [regenerateResults, setRegenerateResults] = useState({}); // { [email]: { resetLink } | { error } }
  const [regenerateCopiedEmail, setRegenerateCopiedEmail] = useState(null);
  const [billingOrgId, setBillingOrgId] = useState(null);
  const [testIds, setTestIds] = useState(new Set());
  // Collapsible sections - every tutor and the support log start closed
  const [expandedOrgs, setExpandedOrgs] = useState({});
  const [showSupportLog, setShowSupportLog] = useState(false);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [orgDocs, allUsers, allCohorts, log, aiUsage, testAccountIds, deletedCohorts] = await Promise.all([
        getAllOrgs(),
        getAllUserProfiles(),
        getAllCohorts({ allOrgs: true }),
        getSupportAccessLog(),
        getAllAiSummaryUsage(),
        getTestAccountIds().catch(err => { console.error('Test accounts load error:', err); return new Set(); }),
        getAllRecentlyDeletedCohorts().catch(err => { console.error('Deleted cohorts load error:', err); return []; })
      ]);
      setSupportLog(log);
      setTestIds(testAccountIds);

      const aiUsageByOrgId = {};
      aiUsage.forEach(u => { aiUsageByOrgId[u.orgId] = u; });

      const orgNameById = {};
      orgDocs.forEach(o => { orgNameById[o.id] = o.name || o.id; });
      // Aileen's org predates the orgs collection existing at all for some
      // deployments - fall back to a readable label if the doc is missing.
      if (!orgNameById[DEFAULT_ORG_ID]) orgNameById[DEFAULT_ORG_ID] = 'Aileen Edgar ACCA ATX';

      const orgIds = new Set([...Object.keys(orgNameById), ...allCohorts.map(c => c.orgId || DEFAULT_ORG_ID), ...allUsers.map(u => u.orgId || DEFAULT_ORG_ID)]);

      const built = Array.from(orgIds).map(orgId => {
        const orgCohorts = allCohorts.filter(c => (c.orgId || DEFAULT_ORG_ID) === orgId);
        const orgUsers = allUsers.filter(u => (u.orgId || DEFAULT_ORG_ID) === orgId);
        const students = orgUsers.filter(u => u.role === 'student' && !u.deleted && !u.pendingDeletion);
        const headMarkers = orgUsers.filter(u => u.role === 'head_marker');
        const markers = orgUsers.filter(u => u.role === 'marker');

        const cohorts = orgCohorts.map(c => ({
          id: c.id,
          name: c.name,
          code: c.code,
          active: c.active !== false,
          students: students.filter(s => s.cohortId === c.id)
        }));

        // Students whose cohortId doesn't match any existing cohort doc
        // (e.g. the cohort was deleted) - still count them, grouped separately.
        const cohortIds = new Set(orgCohorts.map(c => c.id));
        const orphanStudents = students.filter(s => !cohortIds.has(s.cohortId));

        return {
          orgId,
          name: orgNameById[orgId] || orgId,
          headMarkers,
          markers,
          cohorts,
          orphanStudents,
          deletedCohorts: deletedCohorts
            .filter(c => (c.orgId || DEFAULT_ORG_ID) === orgId)
            .sort((a, b) => (b.pendingDeletionAt?.toMillis?.() || 0) - (a.pendingDeletionAt?.toMillis?.() || 0)),
          students,
          aiUsage: aiUsageByOrgId[orgId] || null
        };
      }).sort((a, b) => a.name.localeCompare(b.name));

      setOrgs(built);
    } catch (err) {
      console.error('Platform admin data load error:', err);
      setError('Could not load platform data. You may not have platform admin access.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  // Test accounts (see handleToggleTestAccount) are left out of every count
  const realStudents = (students) => students.filter(s => !testIds.has(s.id));
  const totalStudents = orgs.reduce((sum, o) => sum + realStudents(o.students).length, 0);

  const handleToggleTestAccount = async (uid, name) => {
    const isTest = !testIds.has(uid);
    try {
      await setTestAccount(uid, isTest, name);
      setTestIds(prev => {
        const next = new Set(prev);
        if (isTest) next.add(uid); else next.delete(uid);
        return next;
      });
    } catch (err) {
      console.error('Set test account error:', err);
      alert('Could not update the test account flag: ' + err.message);
    }
  };
  const totalAiCalls = orgs.reduce((sum, o) => sum + (o.aiUsage?.totalCalls || 0), 0);
  const totalAiCostUsd = orgs.reduce((sum, o) => sum + (o.aiUsage?.totalCostUsd || 0), 0);

  // Same as the tutor deleting/restoring it themselves (see
  // adminCohortAction) - typed-name confirmation names the tutor
  const handleCohortAction = async (org, cohort, action) => {
    if (action === 'delete' || action === 'deleteNow') {
      const message = action === 'delete'
        ? `Delete "${cohort.name}" from ${org.name}?\n\nIt moves to ${org.name}'s Recently Deleted. Its students, submissions and files are kept for ${COHORT_DELETION_RETENTION_DAYS} days, and ${org.name} or you can restore it in that time - after that it's permanently deleted. Students who have moved on to a newer cohort keep their work.\n\nType the cohort name to confirm:`
        : `PERMANENTLY delete "${cohort.name}" from ${org.name} right now?\n\nIts students (unless they've moved to another cohort), submissions, marked scripts and files are deleted immediately. This cannot be undone.\n\nType the cohort name to confirm:`;
      const typed = window.prompt(message);
      if (typed === null) return;
      if (typed.trim() !== (cohort.name || '').trim()) {
        alert('Cohort name did not match. Nothing was deleted.');
        return;
      }
    }
    try {
      await adminCohortAction(cohort.id, action);
      alert(action === 'delete' ? `"${cohort.name}" moved to ${org.name}'s Recently Deleted.`
        : action === 'restore' ? `"${cohort.name}" restored.`
        : `"${cohort.name}" permanently deleted.`);
      loadData();
    } catch (err) {
      console.error('Cohort action error:', err);
      alert('Failed: ' + err.message);
    }
  };

  const toggleCohort = (key) => {
    setExpandedCohorts(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const handleCreateOrg = async () => {
    setCreateError('');

    if (!form.orgId.trim() || !form.orgName.trim() || !form.headMarkerName.trim() || !form.headMarkerEmail.trim()) {
      setCreateError('Org ID, org name, head marker name, and head marker email are all required.');
      return;
    }
    if (!/^[a-z0-9-]+$/.test(form.orgId.trim())) {
      setCreateError('Org ID must be lowercase letters, numbers, and hyphens only.');
      return;
    }

    if (!window.confirm(
      `Create a real account for ${form.headMarkerName} <${form.headMarkerEmail}>?\n\n` +
      `This creates a genuine login - you'll get a one-time link afterward to send her ` +
      `so she can set her own password.`
    )) {
      return;
    }

    setCreating(true);
    try {
      const functions = getFunctions(getApp());
      const onboardNewOrg = httpsCallable(functions, 'onboardNewOrg');
      const result = await onboardNewOrg({
        orgId: form.orgId.trim(),
        orgName: form.orgName.trim(),
        headMarkerName: form.headMarkerName.trim(),
        headMarkerEmail: form.headMarkerEmail.trim(),
        cohortName: form.cohortName.trim() || undefined,
        cohortCode: form.cohortCode.trim() || undefined
      });
      setCreateResult(result.data);
      setForm(emptyForm);
      loadData();
    } catch (err) {
      console.error('Onboard new org error:', err);
      setCreateError(err.message || 'Could not create the new tutor. Please try again.');
    } finally {
      setCreating(false);
    }
  };

  const handleCopyLink = async () => {
    if (!createResult?.resetLink) return;
    try {
      await navigator.clipboard.writeText(createResult.resetLink);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (e) {
      console.error('Copy failed:', e);
    }
  };

  // Firebase's password-reset links are single-use and expire on their own -
  // there's no way to "resend" an old one, only mint a fresh one. Doesn't
  // touch the account/org/cohort at all, just generates a new link.
  const handleRegenerateLink = async (email) => {
    setRegeneratingEmail(email);
    setRegenerateResults(prev => ({ ...prev, [email]: undefined }));
    try {
      const functions = getFunctions(getApp());
      const regeneratePasswordResetLink = httpsCallable(functions, 'regeneratePasswordResetLink');
      const result = await regeneratePasswordResetLink({ email });
      setRegenerateResults(prev => ({ ...prev, [email]: { resetLink: result.data.resetLink } }));
    } catch (err) {
      console.error('Regenerate reset link error:', err);
      setRegenerateResults(prev => ({ ...prev, [email]: { error: err.message || 'Could not generate a new link.' } }));
    } finally {
      setRegeneratingEmail(null);
    }
  };

  const handleCopyRegeneratedLink = async (email) => {
    const link = regenerateResults[email]?.resetLink;
    if (!link) return;
    try {
      await navigator.clipboard.writeText(link);
      setRegenerateCopiedEmail(email);
      setTimeout(() => setRegenerateCopiedEmail(null), 2000);
    } catch (e) {
      console.error('Copy failed:', e);
    }
  };

  // Signs Stuart in as the tutor's own real account via a short-lived
  // custom token (see mintSupportSessionToken in functions/index.js) - not
  // a separate view, HeadMarkerDashboard renders exactly as it does for
  // her. Stashes just enough in sessionStorage for App.js to show a
  // "Viewing as..." banner and offer the way back.
  //
  // browserSessionPersistence makes this sign-in itself tab-scoped, not just
  // the sessionStorage banner flag - without it, Firebase Auth's default
  // LOCAL persistence would keep Stuart signed in as the tutor in IndexedDB
  // even after the tab closes, so reopening the app would silently log him
  // back in as her with no banner and no way to tell (found 2026-09-14).
  const handleEnterDashboard = async (orgId) => {
    setEnterError(prev => ({ ...prev, [orgId]: '' }));
    setEnteringOrgId(orgId);
    try {
      const functions = getFunctions(getApp());
      const mintSupportSessionToken = httpsCallable(functions, 'mintSupportSessionToken');
      const result = await mintSupportSessionToken({ orgId });
      const { token, logId, orgName, headMarkerEmail } = result.data;
      sessionStorage.setItem(SUPPORT_SESSION_KEY, JSON.stringify({ logId, orgName, headMarkerEmail }));
      await setPersistence(auth, browserSessionPersistence);
      await signInWithCustomToken(auth, token);
    } catch (err) {
      console.error('Enter dashboard error:', err);
      setEnterError(prev => ({ ...prev, [orgId]: err.message || 'Could not enter this dashboard.' }));
      setEnteringOrgId(null);
    }
  };

  return (
    <div style={{ minHeight: '100vh', background: '#f7fafc', padding: '24px' }}>
      <div style={{ maxWidth: '960px', margin: '0 auto' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
          <div>
            <button
              onClick={onExit}
              style={{
                display: 'flex', alignItems: 'center', gap: '6px',
                background: 'none', border: 'none', color: '#4a5568',
                fontSize: '14px', cursor: 'pointer', marginBottom: '8px', padding: 0
              }}
            >
              <ArrowLeft size={16} /> Back to my dashboard
            </button>
            <h1 style={{ margin: 0, fontSize: '28px' }}>Platform Admin</h1>
            <p style={{ margin: '4px 0 0', color: '#718096', fontSize: '14px' }}>
              Read-only: orgs, cohorts, and student rosters. No submissions, marking, or comments.
            </p>
          </div>
          <div style={{ display: 'flex', gap: '10px' }}>
            <button
              onClick={() => window.open(`https://console.firebase.google.com/project/${getApp().options.projectId}/usage`, '_blank')}
              title="Opens the Firebase Console's Usage and billing page for this project, in a new tab"
              style={{
                display: 'flex', alignItems: 'center', gap: '6px',
                padding: '10px 16px', background: 'white', color: '#2b6cb0',
                border: '2px solid #2b6cb0', borderRadius: '8px', fontSize: '14px', fontWeight: '600', cursor: 'pointer'
              }}
            >
              <CreditCard size={16} /> Firebase Billing
            </button>
            <button
              onClick={onLogout}
              style={{
                display: 'flex', alignItems: 'center', gap: '6px',
                padding: '10px 16px', background: '#e53e3e', color: 'white',
                border: 'none', borderRadius: '8px', fontSize: '14px', cursor: 'pointer'
              }}
            >
              <LogOut size={16} /> Logout
            </button>
          </div>
        </div>

        {loading && <div style={{ color: '#718096' }}>Loading...</div>}
        {error && (
          <div style={{ padding: '16px', background: '#fff5f5', border: '2px solid #feb2b2', borderRadius: '8px', color: '#c53030', marginBottom: '24px' }}>
            {error}
          </div>
        )}

        {!loading && !error && (
          <>
            <div style={{ display: 'flex', gap: '16px', marginBottom: '24px' }}>
              <div style={{ flex: 1, background: 'white', borderRadius: '12px', padding: '20px', boxShadow: '0 2px 8px rgba(0,0,0,0.05)' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#718096', fontSize: '13px', fontWeight: '600', marginBottom: '8px' }}>
                  <Users size={16} /> ORGS
                </div>
                <div style={{ fontSize: '32px', fontWeight: '700' }}>{orgs.length}</div>
              </div>
              <div style={{ flex: 1, background: 'white', borderRadius: '12px', padding: '20px', boxShadow: '0 2px 8px rgba(0,0,0,0.05)' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#718096', fontSize: '13px', fontWeight: '600', marginBottom: '8px' }}>
                  <GraduationCap size={16} /> UNIQUE ACTIVE STUDENTS (ALL ORGS)
                </div>
                <div style={{ fontSize: '32px', fontWeight: '700' }}>{totalStudents}</div>
              </div>
              <div style={{ flex: 1, background: 'white', borderRadius: '12px', padding: '20px', boxShadow: '0 2px 8px rgba(0,0,0,0.05)' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#718096', fontSize: '13px', fontWeight: '600', marginBottom: '8px' }}>
                  <Sparkles size={16} /> AI SUMMARY COST (ALL ORGS)
                </div>
                <div style={{ fontSize: '32px', fontWeight: '700' }}>${totalAiCostUsd.toFixed(2)}</div>
                <div style={{ fontSize: '12px', color: '#a0aec0', marginTop: '4px' }}>{totalAiCalls} call{totalAiCalls === 1 ? '' : 's'} total</div>
              </div>
            </div>

            <div style={{ background: 'white', borderRadius: '12px', padding: '20px', boxShadow: '0 2px 8px rgba(0,0,0,0.05)', marginBottom: '24px' }}>
              <button
                onClick={() => { setShowCreateForm(!showCreateForm); setCreateError(''); setCreateResult(null); }}
                style={{
                  display: 'flex', alignItems: 'center', gap: '8px',
                  padding: '10px 16px', background: '#2b6cb0', color: 'white',
                  border: 'none', borderRadius: '8px', fontSize: '14px', fontWeight: '600', cursor: 'pointer'
                }}
              >
                <UserPlus size={16} /> {showCreateForm ? 'Cancel' : 'Create New Tutor'}
              </button>

              {showCreateForm && !createResult && (
                <div style={{ marginTop: '16px', display: 'grid', gap: '12px', maxWidth: '480px' }}>
                  <label style={{ fontSize: '13px', fontWeight: '600' }}>
                    Org ID (permanent, internal - lowercase, numbers, hyphens only)
                    <input
                      type="text"
                      value={form.orgId}
                      onChange={(e) => setForm({ ...form, orgId: e.target.value })}
                      placeholder="jane-doe-tuition"
                      style={inputStyle}
                    />
                  </label>
                  <label style={{ fontSize: '13px', fontWeight: '600' }}>
                    Org display name
                    <input
                      type="text"
                      value={form.orgName}
                      onChange={(e) => setForm({ ...form, orgName: e.target.value })}
                      placeholder="Jane Doe Tuition"
                      style={inputStyle}
                    />
                  </label>
                  <label style={{ fontSize: '13px', fontWeight: '600' }}>
                    Her name
                    <input
                      type="text"
                      value={form.headMarkerName}
                      onChange={(e) => setForm({ ...form, headMarkerName: e.target.value })}
                      placeholder="Jane Doe"
                      style={inputStyle}
                    />
                  </label>
                  <label style={{ fontSize: '13px', fontWeight: '600' }}>
                    Her email (becomes her login)
                    <input
                      type="email"
                      value={form.headMarkerEmail}
                      onChange={(e) => setForm({ ...form, headMarkerEmail: e.target.value })}
                      placeholder="jane@example.com"
                      style={inputStyle}
                    />
                  </label>
                  <label style={{ fontSize: '13px', fontWeight: '600' }}>
                    First cohort name (optional)
                    <input
                      type="text"
                      value={form.cohortName}
                      onChange={(e) => setForm({ ...form, cohortName: e.target.value })}
                      placeholder="September2026"
                      style={inputStyle}
                    />
                  </label>
                  <label style={{ fontSize: '13px', fontWeight: '600' }}>
                    First cohort code (optional, required if cohort name given)
                    <input
                      type="text"
                      value={form.cohortCode}
                      onChange={(e) => setForm({ ...form, cohortCode: e.target.value })}
                      placeholder="SEPT2026"
                      style={inputStyle}
                    />
                  </label>

                  {createError && (
                    <div style={{ padding: '10px', background: '#fff5f5', border: '1px solid #feb2b2', borderRadius: '6px', color: '#c53030', fontSize: '13px' }}>
                      {createError}
                    </div>
                  )}

                  <button
                    onClick={handleCreateOrg}
                    disabled={creating}
                    style={{
                      padding: '12px 20px',
                      background: creating ? '#a0aec0' : '#1b998b',
                      color: 'white', border: 'none', borderRadius: '8px',
                      fontSize: '14px', fontWeight: '600',
                      cursor: creating ? 'not-allowed' : 'pointer'
                    }}
                  >
                    {creating ? 'Creating...' : 'Create'}
                  </button>
                </div>
              )}

              {createResult && (
                <div style={{ marginTop: '16px', maxWidth: '560px' }}>
                  <div style={{ padding: '12px', background: '#f0fff4', border: '1px solid #9ae6b4', borderRadius: '8px', marginBottom: '12px' }}>
                    <div style={{ fontWeight: '600', marginBottom: '6px' }}>Done - {createResult.orgId}</div>
                    <ul style={{ margin: 0, paddingLeft: '18px', fontSize: '13px', color: '#4a5568' }}>
                      {createResult.steps.map((s, i) => <li key={i}>{s}</li>)}
                    </ul>
                  </div>

                  {createResult.resetLink ? (
                    <>
                      <div style={{ fontSize: '13px', fontWeight: '600', marginBottom: '6px' }}>
                        Send this link to {createResult.headMarkerEmail} so she can set her own password:
                      </div>
                      <div style={{ display: 'flex', gap: '8px' }}>
                        <input
                          type="text"
                          readOnly
                          value={createResult.resetLink}
                          style={{ ...inputStyle, marginTop: 0, flex: 1, fontFamily: 'monospace', fontSize: '12px' }}
                          onClick={(e) => e.target.select()}
                        />
                        <button
                          onClick={handleCopyLink}
                          style={{
                            padding: '0 16px', background: copied ? '#38a169' : '#2b6cb0', color: 'white',
                            border: 'none', borderRadius: '8px', cursor: 'pointer',
                            display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', fontWeight: '600'
                          }}
                        >
                          {copied ? <Check size={14} /> : <Copy size={14} />} {copied ? 'Copied' : 'Copy'}
                        </button>
                      </div>
                    </>
                  ) : (
                    <div style={{ fontSize: '13px', color: '#c53030' }}>
                      Couldn't generate a reset link automatically - check the steps above and generate one manually from Firebase Console &gt; Authentication.
                    </div>
                  )}

                  <button
                    onClick={() => { setShowCreateForm(false); setCreateResult(null); }}
                    style={{
                      marginTop: '16px', padding: '8px 16px', background: 'none',
                      border: '1px solid #cbd5e0', borderRadius: '8px', cursor: 'pointer', fontSize: '13px'
                    }}
                  >
                    Done
                  </button>
                </div>
              )}
            </div>

            <InvoicingPanel orgs={orgs} />

            {orgs.map(org => (
              <div key={org.orgId} style={{ background: 'white', borderRadius: '12px', padding: '20px', boxShadow: '0 2px 8px rgba(0,0,0,0.05)', marginBottom: '16px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
                  <button
                    onClick={() => setExpandedOrgs(prev => ({ ...prev, [org.orgId]: !prev[org.orgId] }))}
                    style={{ display: 'flex', alignItems: 'center', gap: '8px', background: 'none', border: 'none', padding: 0, cursor: 'pointer', color: '#2d3748', textAlign: 'left' }}
                  >
                    {expandedOrgs[org.orgId] ? <ChevronDown size={20} /> : <ChevronRight size={20} />}
                    <h2 style={{ margin: 0, fontSize: '20px' }}>{org.name}</h2>
                  </button>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <span style={{ fontSize: '13px', color: '#718096' }}>
                      {realStudents(org.students).length} student{realStudents(org.students).length === 1 ? '' : 's'}
                      {org.students.length > realStudents(org.students).length && ` (+${org.students.length - realStudents(org.students).length} test)`}
                    </span>
                    <button
                      onClick={() => {
                        setBillingOrgId(billingOrgId === org.orgId ? null : org.orgId);
                        setExpandedOrgs(prev => ({ ...prev, [org.orgId]: true }));
                      }}
                      style={{
                        display: 'flex', alignItems: 'center', gap: '6px',
                        padding: '6px 12px',
                        background: billingOrgId === org.orgId ? '#2b6cb0' : 'white',
                        color: billingOrgId === org.orgId ? 'white' : '#2b6cb0',
                        border: '1px solid #2b6cb0', borderRadius: '6px',
                        fontSize: '13px', fontWeight: '600', cursor: 'pointer'
                      }}
                    >
                      <Receipt size={14} /> Billing
                    </button>
                    {org.orgId !== DEFAULT_ORG_ID && (
                      <button
                        onClick={() => handleEnterDashboard(org.orgId)}
                        disabled={enteringOrgId === org.orgId}
                        style={{
                          display: 'flex', alignItems: 'center', gap: '6px',
                          padding: '6px 12px',
                          background: enteringOrgId === org.orgId ? '#a0aec0' : '#1b998b',
                          color: 'white', border: 'none', borderRadius: '6px',
                          fontSize: '13px', fontWeight: '600',
                          cursor: enteringOrgId === org.orgId ? 'not-allowed' : 'pointer'
                        }}
                      >
                        <LifeBuoy size={14} /> {enteringOrgId === org.orgId ? 'Entering...' : 'Enter dashboard'}
                      </button>
                    )}
                  </div>
                </div>
                {enterError[org.orgId] && (
                  <div style={{ fontSize: '13px', color: '#c53030', marginTop: '8px' }}>
                    {enterError[org.orgId]}
                  </div>
                )}
                {expandedOrgs[org.orgId] && (
                <div style={{ marginTop: '12px' }}>
                <div style={{ fontSize: '13px', color: '#a0aec0', marginBottom: '4px' }}>
                  {org.orgId} · {org.headMarkers.length} head marker{org.headMarkers.length === 1 ? '' : 's'} · {org.markers.length} marker{org.markers.length === 1 ? '' : 's'}
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', color: '#805ad5', marginBottom: '16px' }}>
                  <Sparkles size={13} />
                  {org.aiUsage
                    ? `${org.aiUsage.totalCalls} AI summar${org.aiUsage.totalCalls === 1 ? 'y' : 'ies'} · $${org.aiUsage.totalCostUsd.toFixed(3)} (avg $${(org.aiUsage.totalCostUsd / org.aiUsage.totalCalls).toFixed(4)}/call)`
                    : 'No AI summaries generated yet'}
                </div>
                {billingOrgId === org.orgId && (
                  <BillingReport orgId={org.orgId} orgName={org.name} onClose={() => setBillingOrgId(null)} testIds={testIds} onToggleTestAccount={handleToggleTestAccount} />
                )}

                {org.headMarkers.length > 0 && (
                  <div style={{ marginBottom: '16px' }}>
                    {org.headMarkers.map(h => (
                      <div key={h.email} style={{ marginBottom: '6px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', fontSize: '13px', color: '#4a5568' }}>
                          <span>{h.name} &lt;{h.email}&gt;</span>
                          <button
                            onClick={() => handleRegenerateLink(h.email)}
                            disabled={regeneratingEmail === h.email}
                            style={{
                              padding: '3px 10px',
                              background: 'none',
                              color: '#2b6cb0',
                              border: '1px solid #2b6cb0',
                              borderRadius: '6px',
                              fontSize: '12px',
                              fontWeight: '600',
                              cursor: regeneratingEmail === h.email ? 'not-allowed' : 'pointer'
                            }}
                          >
                            {regeneratingEmail === h.email ? 'Generating…' : 'Generate New Link'}
                          </button>
                        </div>

                        {regenerateResults[h.email]?.resetLink && (
                          <div style={{ display: 'flex', gap: '8px', marginTop: '6px', maxWidth: '560px' }}>
                            <input
                              type="text"
                              readOnly
                              value={regenerateResults[h.email].resetLink}
                              style={{ ...inputStyle, marginTop: 0, flex: 1, fontFamily: 'monospace', fontSize: '12px' }}
                              onClick={(e) => e.target.select()}
                            />
                            <button
                              onClick={() => handleCopyRegeneratedLink(h.email)}
                              style={{
                                padding: '0 16px',
                                background: regenerateCopiedEmail === h.email ? '#38a169' : '#2b6cb0',
                                color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer',
                                display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', fontWeight: '600'
                              }}
                            >
                              {regenerateCopiedEmail === h.email ? <Check size={14} /> : <Copy size={14} />} {regenerateCopiedEmail === h.email ? 'Copied' : 'Copy'}
                            </button>
                          </div>
                        )}
                        {regenerateResults[h.email]?.error && (
                          <div style={{ fontSize: '12px', color: '#c53030', marginTop: '4px' }}>
                            {regenerateResults[h.email].error}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}

                {org.cohorts.length === 0 && org.orphanStudents.length === 0 && (
                  <div style={{ color: '#a0aec0', fontSize: '14px' }}>No cohorts yet.</div>
                )}

                {org.cohorts.map(cohort => {
                  const key = `${org.orgId}|${cohort.id}`;
                  const expanded = !!expandedCohorts[key];
                  return (
                    <div key={cohort.id} style={{ borderTop: '1px solid #edf2f7', paddingTop: '10px', marginTop: '10px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <button
                          onClick={() => toggleCohort(key)}
                          style={{
                            display: 'flex', alignItems: 'center', gap: '6px', flex: 1,
                            background: 'none', border: 'none', cursor: 'pointer', padding: 0,
                            textAlign: 'left', fontSize: '14px', color: '#2d3748'
                          }}
                        >
                          {expanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                          <strong>{cohort.name}</strong>
                          <span style={{ color: '#a0aec0' }}>({cohort.code}){!cohort.active ? ' - inactive' : ''}</span>
                          <span style={{ marginLeft: 'auto', color: '#718096' }}>{realStudents(cohort.students).length} student{realStudents(cohort.students).length === 1 ? '' : 's'}</span>
                        </button>
                        <button
                          onClick={() => handleCohortAction(org, cohort, 'delete')}
                          title="Delete this cohort"
                          style={{ display: 'flex', alignItems: 'center', gap: '4px', padding: '3px 10px', background: 'none', border: '1px solid #e53e3e', color: '#e53e3e', borderRadius: '6px', fontSize: '12px', fontWeight: 600, cursor: 'pointer' }}
                        >
                          <Trash2 size={12} /> Delete
                        </button>
                      </div>
                      {expanded && (
                        <ul style={{ margin: '8px 0 0', paddingLeft: '28px', fontSize: '13px', color: '#4a5568' }}>
                          {cohort.students.length === 0 && <li style={{ color: '#a0aec0' }}>No students yet.</li>}
                          {cohort.students.map(s => (
                            <li key={s.id} style={{ marginBottom: '4px' }}>
                              <span style={{ color: testIds.has(s.id) ? '#a0aec0' : undefined }}>{s.name} - {s.email}</span>
                              {testIds.has(s.id) && (
                                <span style={{ marginLeft: '8px', padding: '1px 8px', background: '#fefcbf', color: '#975a16', borderRadius: '10px', fontSize: '11px', fontWeight: 700 }}>TEST</span>
                              )}
                              <button
                                onClick={() => handleToggleTestAccount(s.id, s.name)}
                                style={{ marginLeft: '8px', padding: '1px 8px', background: 'none', border: '1px solid #cbd5e0', borderRadius: '6px', fontSize: '11px', color: '#4a5568', cursor: 'pointer' }}
                              >
                                {testIds.has(s.id) ? 'Not a test account' : 'Mark as test'}
                              </button>
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  );
                })}

                {org.orphanStudents.length > 0 && (
                  <div style={{ borderTop: '1px solid #edf2f7', paddingTop: '10px', marginTop: '10px', fontSize: '13px', color: '#a0aec0' }}>
                    {org.orphanStudents.length} student{org.orphanStudents.length === 1 ? '' : 's'} not in any current cohort
                  </div>
                )}

                {org.deletedCohorts.length > 0 && (
                  <div style={{ borderTop: '1px solid #edf2f7', paddingTop: '10px', marginTop: '10px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', fontWeight: 600, color: '#718096', marginBottom: '6px' }}>
                      <Trash2 size={13} /> Recently deleted cohorts ({org.deletedCohorts.length})
                    </div>
                    {org.deletedCohorts.map(cohort => {
                      const deletedAt = cohort.pendingDeletionAt?.toDate?.();
                      const daysLeft = deletedAt
                        ? Math.max(0, COHORT_DELETION_RETENTION_DAYS - Math.floor((Date.now() - deletedAt.getTime()) / (1000 * 60 * 60 * 24)))
                        : null;
                      return (
                        <div key={cohort.id} style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap', fontSize: '13px', color: '#a0aec0', marginBottom: '6px' }}>
                          <span style={{ flex: 1 }}>
                            {cohort.name} ({cohort.code})
                            {daysLeft !== null && ` - permanently deleted in ${daysLeft} day${daysLeft === 1 ? '' : 's'}`}
                          </span>
                          <button
                            onClick={() => handleCohortAction(org, cohort, 'restore')}
                            style={{ display: 'flex', alignItems: 'center', gap: '4px', padding: '3px 10px', background: 'none', border: '1px solid #1b998b', color: '#1b998b', borderRadius: '6px', fontSize: '12px', fontWeight: 600, cursor: 'pointer' }}
                          >
                            <RefreshCw size={12} /> Restore
                          </button>
                          <button
                            onClick={() => handleCohortAction(org, cohort, 'deleteNow')}
                            style={{ display: 'flex', alignItems: 'center', gap: '4px', padding: '3px 10px', background: '#e53e3e', border: 'none', color: 'white', borderRadius: '6px', fontSize: '12px', fontWeight: 600, cursor: 'pointer' }}
                          >
                            <Trash2 size={12} /> Delete permanently now
                          </button>
                        </div>
                      );
                    })}
                  </div>
                )}
                </div>
                )}
              </div>
            ))}

            {supportLog.length > 0 && (
              <div style={{ background: 'white', borderRadius: '12px', padding: '20px', boxShadow: '0 2px 8px rgba(0,0,0,0.05)' }}>
                <button
                  onClick={() => setShowSupportLog(!showSupportLog)}
                  style={{ display: 'flex', alignItems: 'center', gap: '8px', background: 'none', border: 'none', padding: 0, cursor: 'pointer', color: '#2d3748', width: '100%', textAlign: 'left' }}
                >
                  {showSupportLog ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
                  <h2 style={{ margin: 0, fontSize: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <LifeBuoy size={16} /> Recent support sessions ({Math.min(supportLog.length, 20)})
                  </h2>
                </button>
                {showSupportLog && (
                <div style={{ display: 'grid', gap: '8px', marginTop: '12px' }}>
                  {supportLog.slice(0, 20).map(entry => (
                    <div key={entry.id} style={{ fontSize: '13px', color: '#4a5568', borderTop: '1px solid #edf2f7', paddingTop: '8px' }}>
                      <strong>{entry.orgName}</strong> — {entry.platformAdminEmail} —{' '}
                      {entry.enteredAt?.toDate ? entry.enteredAt.toDate().toLocaleString('en-GB') : '...'}
                      {entry.exitedAt ? '' : <span style={{ color: '#1b998b', fontWeight: '600' }}> (active)</span>}
                    </div>
                  ))}
                </div>
                )}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

const inputStyle = {
  display: 'block',
  width: '100%',
  marginTop: '4px',
  padding: '10px',
  border: '2px solid #e2e8f0',
  borderRadius: '6px',
  fontSize: '14px',
  fontWeight: 'normal',
  boxSizing: 'border-box'
};

export default PlatformAdminView;
